// apputils.js
import AWS from 'aws-sdk';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import {
  getDetails,
  getSession,
  getUser,
  async_login as _login,
  logout as _logout,
  async_register as _register,
  update_user as _update_user,
  setNewUserCredits
} from './user';
import {
  listObjectsV2,
  async_getObject,
  async_headObject,
  async_getSignedURL
} from './files';
import {
  users,
  s3UserImages,
  s3Templates,
  receipts,
  uploads
} from '../store/actions';
import config from './config.js';
import { constructUser } from './user/helpers';

export const login = async (
  email,
  password,
  remember,
  dispatch,
  fromRegister = false
) => {
  if (!fromRegister) {
    dispatch(users.logging_in());
  }
  try {
    const user = await _login(email, password, remember);
    await setup_user(dispatch, user, fromRegister);
  } catch (e) {
    dispatch(users.login_failed());
  }
};

export const setup_user = async (dispatch, user, fromRegister) => {
  if (typeof user === 'undefined') {
    user = await getUser();
  }
  const defaults = {
    email: '',
    given_name: '',
    address: ''
  };
  if (user !== null) {
    dispatch(users.logged_in(user));
    dispatch(users.getting_details());
    const details = await getDetails(user, defaults);
    dispatch(users.got_details(details));
    !fromRegister && update_credits(dispatch);
  }
};

export const register = async (given_name, email, password, dispatch) => {
  dispatch(users.registering());
  try {
    await _register(given_name, email, password);
    await login(email, password, true, dispatch, true);
    const creditsSet = await setNewUserCredits();
    if (creditsSet.status === 'Success') {
      dispatch(users.got_credits(0));
    }
  } catch (e) {
    console.log(e);
    dispatch(users.register_failed());
  }
};

export const logout = async (user, dispatch) => {
  await _logout(user);
  dispatch(users.logout());
  dispatch(receipts.ready());
};

export const update_user = async (user, attrs, dispatch) => {
  dispatch(users.saving());
  const details = await _update_user(user, attrs);
  dispatch(users.got_details(details));
  // Dismiss notification after 5 seconds
  setTimeout(() => dispatch(users.reset_notification_status()), 5000);
};

export const update_credits = async dispatch => {
  try {
    const credits = await getUserCredits();
    dispatch(users.got_credits(credits));
  } catch (e) {
    console.log(e);
    dispatch(users.get_credits_failed());
  }
};

export const getMyUploads = async dispatch => {
  await getSession();
  const params = {
    Bucket: config.UserBucket,
    Prefix: `p/${AWS.config.credentials.identityId}/`,
    StartAfter: `p/${AWS.config.credentials.identityId}/`
  };
  dispatch(s3UserImages.fetchingUserImages());
  try {
    const uploads = await listObjectsV2(params);
    dispatch(s3UserImages.fetchedUserImages(uploads));
  } catch (e) {
    dispatch(s3UserImages.fetchUserImagesFailed());
    console.log(e);
  }
};

export const getImageMetaData = async (key, dispatch, bucket) => {
  await getSession();
  let fetching;
  let onFail;
  let onSuccess;
  if (bucket === 'TemplateBucket') {
    fetching = uploads.fetchingTemplateMetaData;
    onFail = uploads.fetchTemplateMetaData;
    onSuccess = uploads.fetchedTemplateMetaData;
  } else {
    fetching = s3UserImages.fetchingUserImageMetaData;
    onFail = s3UserImages.fetchUserImageMetaDataFailed;
    onSuccess = s3UserImages.fetchUserImageMetaData;
  }
  const params = {
    Bucket: config[bucket],
    Key: key
  };
  dispatch(fetching());
  try {
    if (!key) throw new Error('Empty template key');
    const metaData = await async_headObject(params);
    dispatch(onSuccess(key, metaData));
  } catch (e) {
    dispatch(onFail());
    console.log(e);
  }
};

export const getSignedS3ImageURL = async (imageKey, thumbKey, dispatch) => {
  await getSession();
  dispatch(s3UserImages.getting_s3_signed_image_url());
  const imageParams = {
    Bucket: config.UserBucket,
    Key: imageKey,
    Expires: 300
  };
  const thumbParams = {
    Bucket: config.UserBucket,
    Key: thumbKey,
    Expires: 300
  };
  try {
    const imageURL = await async_getSignedURL(imageParams);
    const thumbURL = await async_getSignedURL(thumbParams);
    dispatch(
      s3UserImages.get_s3_signed_image_url(
        imageKey,
        thumbKey,
        imageURL,
        thumbURL
      )
    );
  } catch (e) {
    dispatch(s3UserImages.get_s3_signed_image_url_failed());
    console.log(e);
  }
};

export const getUserCredits = async () => {
  await getSession();
  const docClient = new AWS.DynamoDB.DocumentClient();
  const userId = AWS.config.credentials.identityId;
  try {
    const params = {
      TableName: config.UserTable,
      Key: { userId }
    };
    const data = await docClient.get(params).promise();
    if (!data.Item) {
      await setNewUserCredits();
      return 0;
    }
    return data.Item.credits;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getUserId = async () => {
  try {
    const user = await getUser();
    if (user === null) {
      return null;
    }
  } catch (err) {
    console.log({ err });
  }
  await getSession();
  return `${AWS.config.credentials.identityId}`;
};

export const getUserReceipts = async (receipts, dispatch) => {
  await getSession();
  const docClient = new AWS.DynamoDB.DocumentClient();
  const userId = AWS.config.credentials.identityId;
  dispatch(receipts.fetchingReceipts());
  const params = {
    TableName: config.PaymentTable,
    KeyConditionExpression: `userId = :uid`,
    ExpressionAttributeValues: {
      ':uid': userId
    }
  };
  try {
    const data = await docClient.query(params).promise();
    let receiptsData = [];
    if (data.Items) {
      receiptsData = data.Items.sort((a, b) => {
        a = a.date;
        b = b.date;
        if (a > b) return -1;
        else if (b > a) return 1;
        return 0;
      });
    }
    dispatch(receipts.fetchedReceipts(receiptsData));
  } catch (e) {
    console.log(e);
    dispatch(receipts.fetchReceiptsFailed());
  }
};

export const getTemplates = async dispatch => {
  await getSession();
  dispatch(s3Templates.fetchingTemplateJSON());
  const jsonParams = {
    Bucket: config.TemplateBucket,
    Key: 'picula-templates.json'
  };
  try {
    const json = await async_getObject(jsonParams);
    dispatch(s3Templates.fetchedTemplateJSON(json));
  } catch (e) {
    dispatch(s3Templates.fetchTemplateJSONFailed());
    console.log(e);
  }
};

export const sendPasswordResetEmail = async (email, dispatch) => {
  AWS.config.region = config.region;
  AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: config.IdentityPoolId
  });
  const cognitoUser = constructUser(email);
  dispatch(users.sending_recovery_email());
  cognitoUser.forgotPassword({
    onSuccess() {
      // Successfully initiated reset password request
      dispatch(users.sent_recovery_email(email));
    },
    onFailure(err) {
      // Handle the errors to be more user friendly
      let error = '';
      if (err.code === 'LimitExceededException') {
        error = 'Password reset limit exceeded, try again later.';
      } else if (err.code === 'UserNotFoundException') {
        error = 'No user found with this email address.';
      } else {
        error = 'Error: Sun-thing went wrong';
      }
      dispatch(users.send_recovery_email_failed(error));
      console.dir(err || JSON.stringify(err));
    }
  });
};

export const setNewPassword = async (
  email,
  verificationCode,
  newPassword,
  dispatch
) => {
  const cognitoUser = constructUser(email);
  dispatch(users.setting_new_password());
  cognitoUser.confirmPassword(verificationCode, newPassword, {
    onSuccess() {
      dispatch(users.logout());
      dispatch(users.set_new_password());
      // Dismiss notification after 5 seconds
      setTimeout(() => dispatch(users.reset_notification_status()), 5000);
    },
    onFailure(err) {
      dispatch(users.set_new_password_failed(err.message));
      console.dir(err || JSON.stringify(err));
    }
  });
};

export const changePassword = async (oldPassword, newPassword, dispatch) => {
  dispatch(users.changing_password());
  const poolData = {
    UserPoolId: config.UserPoolID,
    ClientId: config.ClientId
  };
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  const cognitoUser = userPool.getCurrentUser();

  if (cognitoUser != null) {
    cognitoUser.getSession(function(err, result) {
      if (result) {
        console.log('You are now logged in.');
        const loginsKey = `cognito-idp.${config.region}.amazonaws.com/${
          config.UserPoolID
        }`;
        // Add the User's Id Token to the Cognito credentials login map.
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: config.IdentityPoolId,
          Logins: {
            [loginsKey]: result.getIdToken().getJwtToken()
          }
        });
      }
    });
  }
  // Call refresh method in order to authenticate user and get new temp credentials
  AWS.config.credentials.refresh(error => {
    if (error) {
      console.error(error);
    }
  });
  cognitoUser.changePassword(oldPassword, newPassword, err => {
    if (err) {
      // Handle the errors to be more user friendly
      let error = '';
      if (err.code === 'LimitExceededException') {
        error = 'Password reset limit exceeded, try again later.';
      } else if (err.code === 'NotAuthorizedException') {
        error = 'Incorrect password.';
      } else {
        error = 'Error: I seem to have got myself in a bit of a flap';
      }
      dispatch(users.change_password_failed(error));
      console.dir(err || JSON.stringify(err));
    } else {
      dispatch(users.change_password());
      // Dismiss notification after 5 seconds
      setTimeout(() => dispatch(users.reset_notification_status()), 5000);
    }
  });
};

export const displayMore = (dispatch, moreButton) => {
  const pageLookUp = {
    homeMoreButton: 'home',
    collectionMoreButton: 'collection',
    libraryMoreButton: 'library',
    setMoreButton: 'set'
  };
  dispatch(users.displaying_more());
  try {
    dispatch(users.display_more(pageLookUp[moreButton]));
  } catch (e) {
    dispatch(users.display_more_failed());
    console.log(e);
  }
};



// WEBPACK FOOTER //
// ./src/utils/apputils.js